import { tr } from '../i18n/i18n';
import moment from 'moment';
import momentTz from 'moment-timezone';
import { getOrgTimeZone } from './tokenManager';

export const formatTime = (date: Date): string => {
  return date.toTimeString().split(' ')[0];
};

/** Promise-based delay, e.g. for a retry backoff wait between attempts. */
export const wait = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });

export const setLocalItemWithExpiry = (key: string, value: string, ttlMs?: number | null) => {
  const now = new Date();

  const item: { value: string; expiry?: number } = { value };
  if (ttlMs) {
    item.expiry = now.getTime() + ttlMs;
  }

  localStorage.setItem(key, JSON.stringify(item));
};

export const getLocalItemWithExpiry = (key: string): string | null => {
  const itemStr = localStorage.getItem(key);
  if (!itemStr) return null;

  try {
    const item = JSON.parse(itemStr);
    const now = new Date().getTime();

    // If expiry exists, check it
    if (item.expiry && now > item.expiry) {
      localStorage.removeItem(key); // Cleanup
      return null;
    }

    return item.value;
  } catch (error) {
    console.error('Failed to parse local item:', error);
    return null;
  }
};

export const setSessionItemWithExpiry = (key: string, value: string, ttlMs: number) => {
  const now = new Date();

  const item = {
    value,
    expiry: now.getTime() + ttlMs, // dynamic expiry time
  };

  sessionStorage.setItem(key, JSON.stringify(item));
};

export const getSessionItemWithExpiry = (key: string): string | null => {
  const itemStr = sessionStorage.getItem(key);
  if (!itemStr) return null;

  try {
    const item = JSON.parse(itemStr);
    const now = new Date().getTime();

    // Check if expired
    if (now > item.expiry) {
      sessionStorage.removeItem(key); // Cleanup
      return null;
    }

    return item.value;
  } catch (error) {
    console.error('Failed to parse session item:', error);
    return null;
  }
};

export const formatDateTime = (date: Date, includeTime: boolean): string => {
  const formattedDate = formatDate(date);
  if (includeTime) {
    const formattedTime = formatTime(date);
    return `${formattedDate} ${formattedTime}`;
  } else {
    return formattedDate;
  }
};

export const calculateAge = (birthDateString: string): string => {
  const birthDate = new Date(birthDateString);
  const today = new Date();

  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDifference = today.getMonth() - birthDate.getMonth();
  const dayDifference = today.getDate() - birthDate.getDate();

  if (monthDifference < 0 || (monthDifference === 0 && dayDifference < 0)) {
    age--;
  }

  return age.toString();
};

export const convertTo12HourFormat = (timestamp: string): string => {
  // Parse the timestamp into a Date object
  const date = new Date(timestamp);
  // Format the hours and minutes
  const hours = date.getHours();
  const minutes = date.getMinutes();
  // Determine AM/PM
  const period = hours >= 12 ? 'PM' : 'AM';
  // Convert to 12-hour format
  const twelveHour = hours % 12 || 12;
  // Format minutes to always be two digits
  const formattedMinutes = minutes.toString().padStart(2, '0');
  // Return the formatted time
  return `${twelveHour}:${formattedMinutes} ${period}`;
};

export function convertTo12HourFormatLocalTime(dateTime: string): string {
  // Parse the dateTime string into a Date object
  const date = new Date(dateTime);

  // Extract hours and minutes
  let hours = date.getHours();
  const minutes = date.getMinutes();

  // Determine AM/PM
  const period = hours >= 12 ? 'PM' : 'AM';

  // Convert hours to 12-hour format
  hours = hours % 12 || 12; // Converts 0 to 12 for 12 AM

  // Format hours and minutes as a 2-digit string
  const formattedHours = hours.toString();
  const formattedMinutes = minutes.toString().padStart(2, '0');

  return `${formattedHours}.${formattedMinutes} ${period}`;
}

export const convertTo24HourFormat = (time: string): string => {
  const [timePart, period] = time.split(' ');
  const [hoursStr, minutesStr] = timePart.split(':');
  let hours = Number(hoursStr);
  const minutes = Number(minutesStr);

  if (period === 'PM' && hours !== 12) {
    hours += 12;
  } else if (period === 'AM' && hours === 12) {
    hours = 0;
  }

  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
};

export const convertTo24HourFormats = (dateTime: string): string => {
  // Parse the date-time string into a Date object
  const date = new Date(dateTime);

  // Extract hours and minutes in 24-hour format
  const hours = date.getHours().toString().padStart(2, '0'); // Ensure 2-digit format
  const minutes = date.getMinutes().toString().padStart(2, '0'); // Ensure 2-digit format

  return `${hours}:${minutes}`;
};

export const formatDate = (dateInput: string | Date, format = 'YYYY-MM-DD'): string => {
  if (!dateInput) return '';
  return moment(dateInput).format(format);
};

/**
 * Format a Date object with a configurable format using moment.js
 * @param date - The date to format
 * @param format - The format string (default: 'YYYY-MM-DD')
 * @returns Formatted date string
 */
export const formatDateWithFormat = (date: Date, format = 'YYYY-MM-DD'): string => {
  return moment(date).format(format);
};

export const formatToOriginalDate = (dateInput: string | Date): Date | null => {
  if (!dateInput) return null;

  const selectedDate = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;

  const day = selectedDate.getDate().toString().padStart(2, '0');
  const month = (selectedDate.getMonth() + 1).toString().padStart(2, '0'); // Month is 0-based
  const year = selectedDate.getFullYear();

  const formattedDateString = `${day}/${month}/${year}`;
  const [formattedDay, formattedMonth, formattedYear] = formattedDateString.split('/');

  // Return a new Date object
  return new Date(parseInt(formattedYear), parseInt(formattedMonth) - 1, parseInt(formattedDay));
};

export const getCookie = (name: string) => {
  const cookies = document.cookie.split('; ').find((row) => row.startsWith(`${name}=`));

  return cookies ? cookies.split('=')[1] : null;
};

export const convertToISO = (time: string): string => {
  // Get today's date in "YYYY-MM-DD" format
  const todayDate = new Date().toISOString().split('T')[0];

  // Combine today's date with the input time in UTC
  const combinedDatetime = `${todayDate}T${time}:00`;

  // Create a new Date object in UTC
  const currentDateTime = moment(combinedDatetime).format('YYYY-MM-DDTHH:mm:ss');

  // Format it to the desired ISO 8601 format
  return currentDateTime;
};

export const convertToISOdateformat = (time: string): string => {
  // Get today's date in "YYYY-MM-DD" format
  const todayDate = new Date().toISOString().split('T')[0];

  // Combine today's date with the input time in UTC
  const combinedDatetime = `${todayDate}T${time}:00`;

  // Create a new Date object in UTC
  const currentDateTime = moment(combinedDatetime).format('YYYY-MM-DDTHH:mm:ss');

  // Format it to the desired ISO 8601 format
  return currentDateTime;
};

export const convertToISODate = (date: string): string => {
  // Get today's date in "YYYY-MM-DD" format

  // Combine today's date with the input time in UTC
  const combinedDatetime = `${date !== '' ? `${date}T00:00:00.000Z` : ``}`;

  // Create a new Date object in UTC

  // Format it to the desired ISO 8601 format
  return combinedDatetime;
};

export const convertToISOFormat = (dateTime: string): string => {
  // Parse the input into a Date object
  const utcDateTime = `${dateTime}:00.00Z`;

  // Return the properly formatted ISO string
  return utcDateTime;
};

export const storeDateInLocalStorage = (dateTime: string | Date | undefined): void => {
  if (!dateTime) {
    console.warn('No dateTime provided to storeDateInLocalStorage');
    return;
  }

  let datePart: string;

  // Handle Date object
  if (dateTime instanceof Date) {
    datePart = dateTime.toISOString().split('T')[0];
  }
  // Handle string
  else if (typeof dateTime === 'string') {
    datePart = dateTime.split('T')[0];
  }
  // Fallback
  else {
    console.warn('Invalid dateTime type provided to storeDateInLocalStorage:', typeof dateTime);
    return;
  }

  localStorage.setItem('storedDate', datePart); // Store date in localStorage
};

export const combineDateAndTime = (time: string): string | null => {
  const storedDate = localStorage.getItem('storedDate'); // Retrieve the stored date
  if (!storedDate) {
    console.error('No stored date found in localStorage!');
    return null;
  }

  // Combine the date with the given time and format
  const combinedDateTime = moment(`${storedDate}T${time}:00`).format('YYYY-MM-DDTHH:mm:ss'); // Add seconds, milliseconds, and Z
  return combinedDateTime;
};

export const convertToUTC = (dateStr: string): string => {
  const formattedDateStr = dateStr.includes('T') ? dateStr : `${dateStr}T00:00:00.000Z`;

  // Create the date object from the given dateStr
  const date = new Date(formattedDateStr);

  // Check if the date is valid
  if (isNaN(date.getTime())) {
    console.error('Invalid date:', formattedDateStr);
    return ''; // Return an empty string if the date is invalid
  }

  const currentTime = new Date(); // Get current time
  const currentHours = currentTime.getUTCHours();
  const currentMinutes = currentTime.getUTCMinutes();
  const currentSeconds = currentTime.getUTCSeconds();

  // Create the date object from the given dateStr
  // const date = new Date(dateStr);

  // Set the current time (hours, minutes, and seconds) on the given date
  date.setUTCHours(currentHours, currentMinutes, currentSeconds, 0);

  // Get the ISO string
  let isoString = date?.toISOString();

  // Replace "000Z" with "00z"
  isoString = isoString.replace('000Z', '00Z');

  return isoString;
};

export const formatDateToISO = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-based, so add 1
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Parse a 'YYYY-MM-DD' string into a Date at LOCAL midnight — the exact inverse
 * of formatDateToISO, which reads local getters.
 *
 * `new Date('2026-03-04')` parses date-only strings as UTC midnight, so anyone
 * west of UTC gets the previous calendar day back. Use this wherever a
 * date-only string is turned back into a Date for a calendar input or filter.
 */
export const parseISODateLocal = (date: string): Date | null => {
  if (!date) return null;
  const parsed = moment(date, 'YYYY-MM-DD', true);
  return parsed.isValid() ? parsed.toDate() : null;
};

export const getCurrentFormattedTime = (date?: string, format = 'YYYY-MM-DDTHH:mm:ss'): string => {
  return moment(date || new Date()).format(format);
};

export const postFormatTimestamp = (timestamp: string): string => {
  if (!timestamp?.trim()) return '';

  const date = moment(timestamp);
  if (!date.isValid()) return '';

  const now = moment();
  const diffInHours = now.diff(date, 'hours');

  // 1. Handle 'Just now' specifically if you want it stricter than moment's default
  if (now.diff(date, 'minutes') < 1) {
    return tr('g.just_now', 'Just now');
  }

  // 2. If it's today or yesterday, use moment's relative time logic
  // .fromNow() returns "X minutes ago", "an hour ago", "a day ago"
  if (diffInHours < 48) {
    // calendar() is also great here as it returns "Today at...", "Yesterday at..."
    // But for your specific requirement:
    if (date.isSame(now, 'day')) return date.fromNow();
    if (date.isSame(now.clone().subtract(1, 'day'), 'day')) return tr('g.yesterday', 'Yesterday');
  }

  // 3. For older dates, return formatted string
  return date.format('MMM D, YYYY');
};

export const formatBillingDate = (dateString: string): string => {
  const tz = getOrgTimeZone() || 'UTC';
  return momentTz(dateString).tz(tz).format('DD MMM YYYY');
};

export const TimeConverter = (timestamp: string) => {
  const localDate = new Date(timestamp); // Uses local time
  return localDate.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
};

export const getRemainingDays = (startDateString: string | undefined | null): number | null => {
  if (!startDateString) return null;

  const startDate = new Date(startDateString);
  const today = new Date();

  const diffTime = startDate.getTime() - today.getTime();
  const diffDays = diffTime / (1000 * 60 * 60 * 24);

  return Math.ceil(diffDays);
};

export const formatScore = (value?: number | null) => {
  if (value === undefined || value === null) return;

  return `${Math.round(value)}%`;
};

/**
 * Format date and time for document display (e.g., "Apr 7, 2026, 02:30 PM")
 * @param dateStr - ISO date string, Date object, or null/undefined
 * @returns Formatted date-time string or '-' if input is null/undefined
 */
export const formatDocumentDateTime = (dateStr?: string | null): string => {
  if (!dateStr) return '-';
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch (error) {
    console.error('Error formatting document datetime:', error);
    return '-';
  }
};

/**
 * Returns a contrasting text color (#1F2937 dark or #FFFFFF white) based on
 * the relative luminance of the given hex background color (WCAG 2.1 formula).
 * Falls back to white if the hex is invalid.
 */

/**
 * Palette colors from the fixed 16-color swatch that are visually perceived
 * as "dark" and therefore require white text for legibility.
 */
const PALETTE_DARK_BACKGROUNDS = new Set([
  '#818CF8', // Indigo
  '#A78BFA', // Violet
  '#C084FC', // Purple
  '#E879F9', // Fuchsia
  '#F472B6', // Pink
]);

export const getContrastTextColor = (hex: string): '#1F2937' | '#FFFFFF' => {
  const normalized = ('#' + hex.replace('#', '')).toUpperCase();

  // Explicit lookup for the fixed 16-color palette where visual perception
  // does not match pure WCAG luminance (all palette colors are bright/vivid).
  if (PALETTE_DARK_BACKGROUNDS.has(normalized)) return '#FFFFFF';

  const cleanHex = hex.replace('#', '');
  if (cleanHex.length !== 6) return '#FFFFFF';

  const r = parseInt(cleanHex.substring(0, 2), 16) / 255;
  const g = parseInt(cleanHex.substring(2, 4), 16) / 255;
  const b = parseInt(cleanHex.substring(4, 6), 16) / 255;

  const toLinear = (c: number) => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);
  const luminance = 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);

  return luminance > 0.179 ? '#1F2937' : '#FFFFFF';
};
